import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Stream from 'flarum/common/utils/Stream';
import Switch from 'flarum/common/components/Switch';
import classList from 'flarum/common/utils/classList';
import { truncate } from 'flarum/common/utils/string';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import humanTime from 'flarum/common/helpers/humanTime';
import icon from 'flarum/common/helpers/icon';
import Notification from 'flarum/forum/components/Notification';
import CommentPost from 'flarum/forum/components/CommentPost';
import PostPreview from 'flarum/forum/components/PostPreview';
import Link from 'flarum/common/components/Link';
import punctuateSeries from 'flarum/common/helpers/punctuateSeries';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import PostUser from 'flarum/forum/components/PostUser';
import TerminalPost from 'flarum/forum/components/TerminalPost';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';

app.initializers.add('crsyyds-anonymous-posting', () => {
  const anonTagId = () => Number(app.forum.attribute('anonymousPostingTagId') || 0);
  const isActorAdmin = () => !!app.session.user && app.session.user.isAdmin();
  const anonName = () => { const v = app.translator.trans('crsyyds-anonymous-posting.lib.anonymous_user'); return Array.isArray(v) ? v.join('') : v; };

  const isAnonPost = (post) => !!(post && post.attribute && post.attribute('isAnonymous'));

  // 同一帖子内的匿名编号（1、2、3…），由服务端按首次发帖顺序给出。
  const anonIndex = (post) => {
    const n = Number(post && post.attribute ? post.attribute('anonymousIndex') : 0);
    return n > 0 ? n : 0;
  };

  // 「匿名用户」/「匿名用户2」
  const anonLabel = (post) => {
    const i = anonIndex(post);
    return anonName() + (i > 0 ? i : '');
  };

  const anonAvatar = (className, label) =>
    <span
      className={classList('Avatar', 'AnonymousAvatar', className)}
      aria-label={label || anonName()}
      title={label || anonName()}
    >
      {(label || anonName()).charAt(0)}
    </span>;

  const tagsIncludeAnon = (tags) => {
    const id = anonTagId();

    return !!id && (tags || []).some((tag) => tag && Number(tag.id()) === id);
  };

  /*
   * ------------------------------------------------------------------
   * 发帖 / 回复时选择是否匿名（仅在匿名区标签下显示开关）
   * ------------------------------------------------------------------
   */
  // Renders the anonymous switch only when `tags` contains the anonymous tag.
  // Tag source differs per composer (see the two extend() registrations below).
  const renderAnonToggle = function (items, tags) {
    if (!tagsIncludeAnon(tags)) return;

    this.isAnonymous = this.isAnonymous || Stream(false);

    items.add('anonymous',
      <div className="AnonymousComposerToggle">
        <Switch state={!!this.isAnonymous()} onchange={this.isAnonymous}>
          {app.translator.trans('crsyyds-anonymous-posting.forum.composer.anonymous_switch')}
        </Switch>
      </div>, -20);
  };

  extend(DiscussionComposer.prototype, 'headerItems', function (items) {
    // New discussion: tags live on composer.fields.tags
    renderAnonToggle.call(this, items, this.composer && this.composer.fields ? this.composer.fields.tags : []);
  });

  extend(ReplyComposer.prototype, 'headerItems', function (items) {
    // Reply: must use the CURRENT discussion's tags. composer.fields.tags is a
    // global leftover (usually an empty array or stale tags from an earlier
    // composer load) and must never be used as the decision source here.
    const fields = this.composer && this.composer.fields;
    const discussion = (fields && fields.discussion) || (this.attrs && this.attrs.discussion);

    renderAnonToggle.call(this, items, discussion ? (discussion.tags() || []) : []);
  });

  extend(DiscussionComposer.prototype, 'data', function (data) {
    if (this.isAnonymous && this.isAnonymous()) {
      data.isAnonymous = true;
    }
  });

  extend(ReplyComposer.prototype, 'data', function (data) {
    if (this.isAnonymous && this.isAnonymous()) {
      data.isAnonymous = true;
    }
  });

  /*
   * ------------------------------------------------------------------
   * 帖子署名：普通用户看到「匿名用户」；管理员看到真实身份 + 匿名徽标
   * ------------------------------------------------------------------
   */
  override(PostUser.prototype, 'view', function (original) {
    const post = this.attrs.post;

    if (post && post.attribute('isAnonymous')) {
      if (isActorAdmin()) {
        const user = post.user();

        if (user) {
          return (
            <div className="PostUser PostUser--anonymousAdmin">
              <h3 className="PostUser-name">
                <a href={app.route.user(user)}>
                  {avatar(user, { className: 'PostUser-avatar' })}
                  {username(user)}
                  <span className="AnonymousBadge">
                    {app.translator.trans('crsyyds-anonymous-posting.forum.badge.anonymous')}
                  </span>
                </a>
              </h3>
            </div>
          );
        }
      }

      return (
        <div className="PostUser PostUser--anonymous">
          <h3 className="PostUser-name">
            {anonAvatar('PostUser-avatar', anonLabel(post))}
            <span className="username">{anonLabel(post)}</span>
          </h3>
        </div>
      );
    }

    return original();
  });

  /*
   * ------------------------------------------------------------------
   * 「X 回复了此帖」列表：匿名回复者显示为「匿名用户N」
   * ------------------------------------------------------------------
   * 服务端已把匿名帖的 user 关系剔除（不会出现真实用户名），这里把「[已删除]」
   * 之类显示为匿名编号，并按编号去重（两个不同的匿名用户不会合并成一个）。
   */
  extend(CommentPost.prototype, 'footerItems', function (items) {
    const post = this.attrs.post;
    const replies = post.mentionedBy && post.mentionedBy();

    if (!replies || !replies.length || !items.has || !items.has('replies')) return;
    if (!replies.some(isAnonPost)) return;
    if (isActorAdmin()) return; // 管理员保留核心行为（真实用户名）

    const keys = [];
    const repliers = replies
      .slice()
      .sort((reply) => (reply.user() === app.session.user ? -1 : 0))
      .filter((reply) => {
        const user = reply.user();
        const key = isAnonPost(reply)
          ? 'a' + (anonIndex(reply) || 'p' + reply.id())
          : user ? 'u' + user.id() : 'x';

        if (keys.indexOf(key) === -1) {
          keys.push(key);
          return true;
        }

        return false;
      });

    const limit = 4;
    const overLimit = post.mentionedByCount() > limit;

    const names = repliers.slice(0, overLimit ? limit - 1 : limit).map((reply) => {
      const user = reply.user();
      const label = isAnonPost(reply)
        ? anonLabel(reply)
        : (app.session.user === user
            ? app.translator.trans('flarum-mentions.forum.post.you_text')
            : username(user));

      return (
        <Link href={app.route.post(reply)} data-number={reply.number()}>
          {label}
        </Link>
      );
    });

    if (overLimit) {
      names.push(app.translator.trans('flarum-mentions.forum.post.others_text', {
        count: post.mentionedByCount() - names.length,
      }));
    }

    const isSelf = repliers[0] && app.session.user === repliers[0].user();

    items.add(
      'replies',
      <div className="Post-mentionedBy">
        <span className="Post-mentionedBy-summary">
          {icon('fas fa-reply')}
          {app.translator.trans(`flarum-mentions.forum.post.mentioned_by${isSelf ? '_self' : ''}_text`, {
            count: names.length,
            users: punctuateSeries(names),
          })}
        </span>
      </div>
    );
  });

  /*
   * ------------------------------------------------------------------
   * 兜底：DOM 层面把残留的提及/回复列表姓名改成匿名编号
   * ------------------------------------------------------------------
   * 主要路径是服务端（contentHtml 重写 + user 关系剔除），这里只处理
   * 已存在于缓存/旧数据里的残留，幂等、无副作用。
   */
  const patchAnonNames = function (root) {
    if (!root || !root.querySelectorAll || isActorAdmin()) return;

    const post = this.attrs.post;
    const replies = post && post.mentionedBy ? post.mentionedBy() : null;

    if (replies && replies.length) {
      const labels = {};
      for (let i = 0; i < replies.length; i++) {
        if (isAnonPost(replies[i])) labels[String(replies[i].number())] = anonLabel(replies[i]);
      }

      const anchors = root.querySelectorAll('.Post-mentionedBy-summary a[data-number]');
      for (let i = 0; i < anchors.length; i++) {
        const label = labels[anchors[i].getAttribute('data-number')];
        if (!label) continue;
        const target = anchors[i].querySelector('.username-text') || anchors[i];
        if (target.textContent.trim() !== label) target.textContent = label;
      }
    }

    const mentions = root.querySelectorAll('a.PostMention[data-id]');
    for (let i = 0; i < mentions.length; i++) {
      const id = mentions[i].getAttribute('data-id');
      const mentioned = id && app.store.getById('posts', id);
      if (mentioned && isAnonPost(mentioned)) {
        const label = anonLabel(mentioned);
        if (mentions[i].textContent.trim() !== label) mentions[i].textContent = label;
      }
    }
  };

  extend(CommentPost.prototype, 'oncreate', function () {
    try { patchAnonNames.call(this, this.element); } catch (e) { /* noop */ }
  });

  extend(CommentPost.prototype, 'onupdate', function () {
    try { patchAnonNames.call(this, this.element); } catch (e) { /* noop */ }
  });

  /*
   * ------------------------------------------------------------------
   * 提及预览（悬浮卡片/弹窗）：匿名帖不显示真实头像与姓名
   * ------------------------------------------------------------------
   */
  override(PostPreview.prototype, 'view', function (original) {
    const post = this.attrs.post;

    if (post && isAnonPost(post) && !isActorAdmin()) {
      return (
        <Link className="PostPreview" href={app.route.post(post)} onclick={this.attrs.onclick}>
          <span className="PostPreview-content">
            {anonAvatar(null, anonLabel(post))}
            {anonLabel(post)} <span className="PostPreview-excerpt">{this.excerpt()}</span>
          </span>
        </Link>
      );
    }

    return original();
  });

  /*
   * ------------------------------------------------------------------
   * 列表页：楼主头像与「发布于/最后回复」署名
   * ------------------------------------------------------------------
   */
  override(DiscussionListItem.prototype, 'authorAvatarView', function (original) {
    const discussion = this.attrs.discussion;

    if (discussion && discussion.attribute('isAnonymous') && !isActorAdmin()) {
      return (
        <span className="DiscussionListItem-author">
          {anonAvatar()}
        </span>
      );
    }

    return original();
  });

  override(TerminalPost.prototype, 'view', function (original) {
    const discussion = this.attrs.discussion;
    const lastPost = this.attrs.lastPost && discussion.replyCount();

    const anon = lastPost
      ? discussion.attribute('lastPostAnonymous')
      : discussion.attribute('isAnonymous');

    if (anon && !isActorAdmin()) {
      const time = discussion[lastPost ? 'lastPostedAt' : 'createdAt']();
      const key = lastPost
        ? 'crsyyds-anonymous-posting.forum.discussion_list.replied_anonymous_text'
        : 'crsyyds-anonymous-posting.forum.discussion_list.started_anonymous_text';

      return (
        <span>
          {!!lastPost && icon('fas fa-reply')}{' '}
          {app.translator.trans(key, { ago: humanTime(time) })}
        </span>
      );
    }

    return original();
  });

  /*
   * ------------------------------------------------------------------
   * 通知：匿名回复/提及不显示真实用户名
   * ------------------------------------------------------------------
   */
  const anonAwareNotification = (textKey, fallback) =>
    class AnonymousAwareNotification extends Notification {
      icon() {
        return fallback.icon;
      }

      href() {
        return fallback.href.call(this);
      }

      content() {
        const notification = this.attrs.notification;

        if (notification && notification.attribute('isAnonymous')) {
          return app.translator.trans(textKey);
        }

        return fallback.content.call(this);
      }

      excerpt() {
        return fallback.excerpt ? fallback.excerpt.call(this) : undefined;
      }
    };

  app.notificationComponents.newPost = anonAwareNotification(
    'crsyyds-anonymous-posting.forum.notifications.new_post_anonymous_text',
    {
      icon: 'fas fa-star',
      href() {
        const notification = this.attrs.notification;
        const discussion = notification.subject();
        const content = notification.content() || {};

        return app.route.discussion(discussion, content.postNumber);
      },
      content() {
        return app.translator.trans('flarum-subscriptions.forum.notifications.new_post_text', {
          user: this.attrs.notification.fromUser(),
        });
      },
    }
  );

  app.notificationComponents.postMentioned = anonAwareNotification(
    'crsyyds-anonymous-posting.forum.notifications.post_mentioned_anonymous_text',
    {
      icon: 'fas fa-reply',
      href() {
        const notification = this.attrs.notification;
        const post = notification.subject();
        const content = notification.content();

        return app.route.discussion(post.discussion(), content && content.replyNumber);
      },
      content() {
        return app.translator.trans('flarum-mentions.forum.notifications.post_mentioned_text', {
          user: this.attrs.notification.fromUser(),
          count: 1,
        });
      },
      excerpt() {
        return truncate((this.attrs.notification.subject().contentPlain() || '') + '', 200);
      },
    }
  );

  app.notificationComponents.userMentioned = anonAwareNotification(
    'crsyyds-anonymous-posting.forum.notifications.user_mentioned_anonymous_text',
    {
      icon: 'fas fa-at',
      href() {
        const post = this.attrs.notification.subject();

        return app.route.discussion(post.discussion(), post.number());
      },
      content() {
        return app.translator.trans('flarum-mentions.forum.notifications.user_mentioned_text', {
          user: this.attrs.notification.fromUser(),
        });
      },
      excerpt() {
        return truncate((this.attrs.notification.subject().contentPlain() || '') + '', 200);
      },
    }
  );
});
